import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import SearchIcon from "@mui/icons-material/Search";
import { SortableTableHead } from "../../common/SortableTableHead";
import {
  SearchIconWrapperPatrols,
  StyledInputBasePatrols,
  SearchPatrols,
} from "./SearchBarPatrols";
import { usePatrolsStore } from "./patrolsStore/patrolsStore";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import DownloadIcon from "@mui/icons-material/Download";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import { downloadPatrolReport } from "./patrolsUtils/downloadPatrolReport";
import { Order } from "../Alerts/AlertsUtils/alertsInterfaces";
import {
  descendingComparator,
  stableSort,
} from "../Alerts/AlertsUtils/alertsTableFunctions";
import { PatrolTableData } from "./patrolsUtils/patrolsInterfaces";

function getPatrolsTableComparator(
  order: Order,
  orderBy: string
): (a: PatrolTableData, b: PatrolTableData) => number {
  const effectiveKey: keyof PatrolTableData =
    orderBy === "patrol_date" || orderBy === "start_time"
      ? "timestamp_unix"
      : (orderBy as keyof PatrolTableData);

  return order === "desc"
    ? (a, b) => descendingComparator(a, b, effectiveKey)
    : (a, b) => -descendingComparator(a, b, effectiveKey);
}

export const PatrolsTable = () => {
  const {
    patrolsData,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    searchQueryPatrols,
    setSearchQueryPatrols,
    setSelectedPatrol,
    setPatrolDialogOpen,
    setSelectedPatrolIndex,
  } = usePatrolsStore();

  // Sort state for table header (default: newest patrol first)
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<string>("patrol_date");

  // Track which patrol is currently downloading
  const [downloadingPatrolId, setDownloadingPatrolId] = useState<string | null>(
    null
  );

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Handle download patrol report
  const handleDownloadPatrolReport = async (patrolId: string) => {
    setDownloadingPatrolId(patrolId);
    try {
      await downloadPatrolReport(patrolId);
    } catch (error) {
      console.error("Error downloading patrol report:", error);
      alert("Unable to download patrol report");
    } finally {
      setDownloadingPatrolId(null);
    }
  };

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter data based on search query
  const filteredData = patrolsData.filter(
    (patrol) =>
      patrol.site?.toLowerCase().includes(searchQueryPatrols.toLowerCase()) ||
      patrol.patrol_date
        ?.toLowerCase()
        .includes(searchQueryPatrols.toLowerCase()) ||
      patrol.start_time
        ?.toLowerCase()
        .includes(searchQueryPatrols.toLowerCase()) ||
      patrol.schedule_name
        ?.toLowerCase()
        .includes(searchQueryPatrols.toLowerCase())
  );

  // Sort filtered data (patrol date / start time use Unix seconds — formatted labels are not sortable)
  const sortedData = stableSort(
    filteredData,
    getPatrolsTableComparator(order, orderBy)
  );

  // Calculate the maximum valid page (minimum of 0 for empty data)
  const maxPage = Math.max(
    0,
    Math.ceil(sortedData.length / rowsPerPage) - 1
  );

  // Clamp page to valid range to prevent MUI TablePagination warning
  // This handles the case when data changes and page is temporarily out of range
  const safePage = Math.min(page, maxPage);

  // Calculate paginated data using safe page value
  const paginatedData = sortedData.slice(
    safePage * rowsPerPage,
    safePage * rowsPerPage + rowsPerPage
  );

  // Update the store's page if it's out of range
  useEffect(() => {
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredData.length, page, maxPage, setPage]);

  return (
    <TableContainer component={Paper}>
      <div>
        <SearchPatrols>
          <SearchIconWrapperPatrols>
            <SearchIcon />
          </SearchIconWrapperPatrols>
          <StyledInputBasePatrols
            value={searchQueryPatrols}
            placeholder="Search Patrols..."
            onChange={(e) => setSearchQueryPatrols(e.target.value)}
            inputProps={{ "aria-label": "search" }}
          />
        </SearchPatrols>
      </div>
      <Table sx={{ fontFamily: "Mulish" }} aria-label="patrols_table">
        <SortableTableHead
          headCells={[
            { id: "patrol_date", label: "Patrol Date" },
            { id: "start_time", label: "Patrol Start Time" },
            { id: "site", label: "Site" },
            { id: "schedule_name", label: "Schedule" },
            { id: "total_alerts", label: "Total Alerts" },
          ]}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          nonSortableCells={[{ label: "" }]}
        />
        <TableBody>
          {paginatedData.length > 0 ? (
            paginatedData.map((patrol, index) => (
              <TableRow
                key={index}
                hover
                onClick={() => {
                  const globalIndex = safePage * rowsPerPage + index;
                  setSelectedPatrol(patrol);
                  setSelectedPatrolIndex(globalIndex);
                  setPatrolDialogOpen(true);
                }}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#e3ecfa !important",
                    "& .MuiTableCell-root": {
                      backgroundColor: "#e3ecfa !important",
                    },
                  },
                }}
              >
                <TableCell sx={{ width: "20%" }}>
                  {patrol.patrol_date || "N/A"}
                </TableCell>
                <TableCell sx={{ width: "20%" }}>
                  {patrol.start_time || "N/A"}
                </TableCell>
                <TableCell sx={{ width: "20%" }}>
                  {patrol.site || "N/A"}
                </TableCell>
                <TableCell sx={{ width: "20%" }}>
                  {patrol.schedule_name?.trim() ? patrol.schedule_name : "—"}
                </TableCell>
                {/* <TableCell sx={{ width: "20%" }}>
                  {patrol.total_cameras || 0}
                </TableCell> */}
                <TableCell sx={{ width: "20%" }}>
                  {patrol.total_alerts ?? 0}
                </TableCell>
                <TableCell sx={{ width: "auto", minWidth: "48px" }}>
                  <OrangeTextButton
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleDownloadPatrolReport(String(patrol.patrol_id));
                    }}
                    text={
                      downloadingPatrolId === patrol.patrol_id ? (
                        <CircularProgress size={20} sx={{ color: "#FF8400" }} />
                      ) : (
                        <Tooltip title={"Download Patrol"} placement="top">
                          <DownloadIcon />
                        </Tooltip>
                      )
                    }
                    disabled={downloadingPatrolId === patrol.patrol_id}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ padding: "20px" }}>
                <div className="font-mulish text-gray-500">
                  {searchQueryPatrols
                    ? "No patrols found matching your search."
                    : "No patrols data available."}
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={sortedData.length}
        rowsPerPage={rowsPerPage}
        page={safePage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  );
};
